<template>
  <div class="view-container">
    <div class="extended-filter-block">
      <h2>Заказы и подписки</h2>
      <div class="extended-search-block-mobile">
        <div class="search-outline-block">
          <SearchOutlined @click="showMobileFilterModal = true" style="color: rgba(0, 0, 0, 0.45)" />
          <div v-if="filterString" class="dot"></div>
        </div>
      </div>
    </div>

    <SubscriptionOptions
      v-if="showOptions || !currentSubscriptionSummary.length"
      @showSubscription="showOptions = false"
      @showOrderForm="showOrderForm = true"
    />

    <CurrentSubscriptionSummary
        v-if="!showOptions && currentSubscriptionSummary.length"
        @showOptions="showOptions = true"
    />

    <div class="card">
      <h2>История заказазов</h2>
      <div class="orders-control">
        <div class="segment-wrapper">
          <a-segmented v-model:value="currentFilter" :options="filterOptions" />
        </div>
        <div>
          <a-button class="primary-button" @click="showOrderForm = true">
            <template #icon>
              <PlusOutlined />
            </template>
            Создать
          </a-button>
        </div>
      </div>

      <div class="orders-table">
        <a-table
            rowKey="id"
            :columns="columns"
            :data-source="tableData"
            :scroll="{ x: scrollX }"
            :pagination="false"
            :loading="tableLoading"
            :table-layout="'fixed'"
            @change="handleTableChange"
            :expandable="expandableConfig"
        >
          <template #expandedRowRender="{ record }">
            <a-table
                class="inner-table"
                :columns="subColumns"
                :data-source="record.subproducts"
                :pagination="false"
                size="small"
                rowKey="key">

              <template #bodyCell="{column, text, record: subRecord }">
                <template v-if="column.dataIndex === 'sum'">
                  <span v-if="subRecord.price"> {{ subRecord.price * (subRecord.count ?? 1)}} </span>
                </template>

                <template v-if="column.dataIndex === 'activated_at'">
                  <span> {{ text ?? record.paid_at ?? '' }} </span>
                </template>

                <template v-if="column.dataIndex === 'type'">
                  <span> {{ productTypeDictionary[text] ?? 'Аналитика' }}</span>
                </template>
              </template>
            </a-table>
          </template>

          <template #bodyCell="{ column, text, record }">
            <template v-if="column.dataIndex === 'status'">
              <a-tag color="green" v-if="record.status === 'paid'">
                Оплачен
              </a-tag>
              <a-tag class="blue-tag" v-else>
                Не оплачен
              </a-tag>
            </template>

            <template v-if="column.dataIndex === 'price'">
              <span v-if="text"> {{ text }} &#8381;</span>
            </template>

            <template v-if="column.dataIndex === 'user'">
              <span v-if="text" class="user-link" @click="router.push('/staff/' + record.user_id + '/edit')"> {{ text }}</span>
            </template>
          </template>

          <template #emptyText>
            <div class="no-data-wrapper">
              <img :src="NotFoundIcon" class="no-data-image" alt="Нет результатов"/>

              <div class="no-data-text">
                {{
                  filterString
                      ? 'Нет данных для отображения. Измените фильтры или запрос.'
                      : 'Нет данных для отображения.'
                }}
              </div>

              <a-button
                  v-if="filterString"
                  @click="resetFilters"
              >
                <template #icon>
                  <ReloadOutlined />
                </template>
                Сбросить фильтры
              </a-button>
            </div>
          </template>
        </a-table>
      </div>

      <div class="pagination-block">
        <a-pagination v-model:current="currentPage"
                      :page-size="entitiesPerPage"
                      :buildOptionText="(count) => `${count.value} / стр`"
                      :show-total
                          ="total => `Всего ${total ?? 0} элементов`"
                      :total="totalOrders"
                      @change="handlePageChange"
        />
      </div>
    </div>

    <SmallUsersFilterMobile
        v-if="showMobileFilterModal"
        :open="showMobileFilterModal"
        @close="showMobileFilterModal = false"
    />

    <NewOrderForm
        v-if="showOrderForm"
        :open="showOrderForm"
        :showOrdersButton="false"
        @close="showOrderForm = false"
        @submitted="fetchAfterSubmit"
    />
  </div>
</template>

<script>
import {onBeforeUnmount, ref, watch} from "@vue/runtime-core";
  import {computed} from "@vue/reactivity";
  import {h} from "vue";
import {ReloadOutlined, SettingOutlined} from "@ant-design/icons-vue";
  import {useStore} from "vuex";
  import {SearchOutlined, PlusOutlined} from "@ant-design/icons-vue";
import SmallUsersFilterMobile from "../../feature/users/forms/SmallUsersFilterMobile.vue";
import SubscriptionOptions from "../Subscription/SubscriptionOptions.vue";
import CurrentSubscriptionSummary from "../Subscription/CurrentSubscriptionSummary.vue";
import CreateOrders from "../Orders/CreateOrders.vue";
import OrderForm from "../../feature/order/create-order/OrderForm.vue";
import NewOrderForm from "../Orders/OrderForm.vue";
import NotFoundIcon from "@/assets/images/icons/no_data.svg";
import {useRouter} from "vue-router";
import useCheckRights from "@/mixins/useCheckRights";
import {useTableColumns} from "@/mixins/useTableColumns";

export default {
    components: {
      ReloadOutlined,
      NewOrderForm,
      OrderForm,
      CreateOrders,
      CurrentSubscriptionSummary, SubscriptionOptions, SmallUsersFilterMobile, SearchOutlined, PlusOutlined, SettingOutlined},
    setup() {
      const store = useStore();
      const router = useRouter();
      const showMobileFilterModal = ref(false);

      const entitiesPerPage = ref(10);
      const currentPage = ref(1);
      const tableLoading = ref(true);
      const showOptions = ref(false);
      const showOrderForm =ref(false);

      const currentSubscriptionSummary = computed(() => store.state.subscription.subscriptionSummary);
      const totalOrders = computed(() => store.state.relations.total);
      const orders = computed(() => store.state.relations.ordersList);
      const tableData = ref();
      const {getRight} = useCheckRights();

      const filterOptions = [
        {
          label: 'Все',
          value: 'all',
        },
        {
          label: 'Новые',
          value: 'new',
        },
        {
          label: 'Оплачены',
          value: 'paid',
        }
      ];

      const currentFilter = ref('all');

      const filters = computed(() => store.state.users.filters);
      const filterString = computed(() => store.state.users.filterString);
      const user = computed(() => store.state.profile.profile);

      const resetFilters = () => {
        store.dispatch('users/setUserFilters', {
          filters: {
            name: '',
            job_title: '',
            department: '',
            online_from: '',
            created_at: '',
            email: '',
            telephone: '',
            groups: [],
            without_group_only: false,
          },
          filterString: null,
        });

        currentPage.value = 1;
      }

      //сортировка
      const sortState = ref({
        field: null,
        order: null,
      });

      const handleTableChange = (pagination, filter, sorter) => {
        sortState.value = {
          field: sorter?.field ?? null,
          order: sorter?.order ?? null,
        };

        fetchOrders();
      }

      const fetchOrders = () => {
        tableLoading.value = true;

        store.dispatch("relations/getOrdersSummary", {
          status: currentFilter.value === 'all' ? null : currentFilter.value,
          per_page: entitiesPerPage.value,
          page: currentPage.value,
          sort_field: sortState.value.field,
          sort_direction:
              sortState.value.order === 'ascend'
                  ? 'asc'
                  : sortState.value.order === 'descend'
                      ? 'desc'
                      : null,
          ...filters.value,
        }).then(() => {
          tableData.value = orders.value.map((el) => {
            return {
              id: el.id,
              created_at: el.created_at,
              status: el.status,
              user: el.user_info?.name ? el.user_info.name : (`${el.user.last_name} ${el.user.first_name} ${el.user.patronymic}`),
              email: el.user_info?.email ? el.user_info.email : el.user.email,
              user_id: el.user.id,
              paid_at: el.paid_at,
              price: el.price,
              subproducts: [
                ...(el.entities ?? []),
                ...(el.products ?? []),
                ...(el.subscriptions ?? []),
              ],
            }
          });
        }).finally(() => {
          tableLoading.value = false;
        });
      }

      const expandableConfig = computed(() => ({
        rowExpandable: (record) =>
            Array.isArray(record.subproducts) && record.subproducts.length > 0,
        width: 20,
      }));

      const initialColumns = ref([
        {
          title: 'ID',
          dataIndex: 'id',
          width: 80,
          sorter: true,
          visible: true,
        },
        {
          title: 'Создан',
          dataIndex: 'created_at',
          sorter: true,
          visible: true,
          width: 250,
        },
        {
          title: 'Стоимость',
          dataIndex: 'price',
          sorter: true,
          visible: true,
          width: 100,
        },
        {
          title: 'Статус',
          dataIndex: 'status',
          visible: true,
          width: 100,
        },
        {
          title: 'Пользователь',
          dataIndex: 'user',
          sorter: false,
          visible: true,
          width: 220,
        },
        {
          title: 'Почта',
          dataIndex: 'email',
          sorter: false,
          visible: true,
          width: 270,
        },
        {
          title: 'Время оплаты',
          dataIndex: 'paid_at',
          sorter: true,
          visible: true,
          width: 150,
        },
      ]);

      const { columns, scrollX } = useTableColumns(initialColumns, {tableKey: 'orders', settingsWidth: 40})

      const productTypeDictionary = {
        test: 'ТЭИ',
        course: 'Развитие',
        reference_profile: 'Эталонный Профиль',
        subscription: 'Подписка',
        tools: 'Инструменты',
      }

      const subColumns = [
        { title: 'Наименование', dataIndex: 'name', width: 150 },
        { title: 'Тип', dataIndex: 'type', width: 80 },
        { title: 'Кол-во', dataIndex: 'count', width: 90 },
        { title: 'Стоимость', dataIndex: 'price', width: 80 },
        { title: 'Сумма', dataIndex: 'sum', width: 120 },
        { title: 'Дата активации', dataIndex: 'activated_at', width: 140 },
        { title: 'Истекает', dataIndex: 'expires_at', width: 150 },
      ];

      const updateFilters = () => {
        showMobileFilterModal.value = false;

        currentPage.value = 1;
        fetchOrders();
      }

      const handlePageChange = (current, perPage) => {
        if (entitiesPerPage.value !== perPage) {
          entitiesPerPage.value = perPage;
          setEntitiesPerPage(entitiesPerPage.value);
        }

        currentPage.value = current;
        fetchOrders();
      }

      const setEntitiesPerPage = (num) => {
        localStorage.setItem('perPage', num)
      }

      watch(() => user.value, () => {
            if (user.value?.company?.id) {
              store.dispatch('subscription/getSubscriptionSummary', user.value?.company?.id)
            }

            if (user.value?.id && getRight('staff')) {
              router.push('/personal')
            }
          },
          { deep: true, immediate: true }
      );

      watch(filters, () => {
            updateFilters();
          },
          { deep: true, immediate: true }
      );

      watch(() => currentFilter.value, () => {
        fetchOrders();
      });

      onBeforeUnmount(() => {
        store.dispatch('users/clearUserFilters');
      });

      const fetchAfterSubmit = () => {
        resetFilters();
      };

      return {
        NotFoundIcon,
        router,
        showMobileFilterModal,
        filterString,
        currentSubscriptionSummary,
        showOptions,
        filterOptions,
        currentFilter,
        showOrderForm,
        fetchAfterSubmit,
        totalOrders,
        currentPage,
        entitiesPerPage,
        handleTableChange,
        handlePageChange,
        columns,
        tableData,
        resetFilters,
        subColumns,
        tableLoading,
        productTypeDictionary,
        expandableConfig,
        scrollX
      }
    }
  }
</script>

<style scoped lang="scss">
  .view-container {
    padding-top: 0;
    margin-top: 0;
  }

  .extended-filter-block {
    display: none;

    @media (max-width: $mobile-breakpoint) {
      margin: 24px 0;
      display: flex;
      column-gap: 25px;
      align-items: center;
      justify-content: space-between;
    }
  }

  .extended-filter-block h2 {
    font-size: 20px;
    margin-bottom: 0;
  }

  .extended-search-block-mobile {
    display: none;
    margin-right: 10px;

    @media(max-width: 768px) {
      display: block;
    }
  }

  :deep(.anticon-reload) {
    cursor: pointer;
  }

  :deep(.anticon-reload:hover) {
    color: var(--main-green) !important;
  }

  .orders-control {
    display: flex;
    justify-content: space-between;
    column-gap: 10px;
    margin-bottom: 16px;
  }

  .search-outline-block {
    position: relative;
  }

  .dot {
    position: absolute;
    top: -1px;
    left: 11px;
    height: 5px;
    width: 5px;
    background-color: var(--color-error);
    border-radius: 50%;
  }

  h2 {
    margin-bottom: 8px;
    font-weight: 600;
    font-size: 16px;
    line-height: 120%;
  }

  .pagination-block {
    margin-left: auto;
    margin-top: 20px;
  }

  :deep(.ant-table-thead >tr>th) {
    padding: 5px 10px !important;
  }

  :deep(.ant-table-tbody>tr:not(.ant-table-measure-row)>td) {
    padding: 10px 5px !important;
  }

  .inner-table {
    background: #fafafa;

    :deep(.ant-table-tbody) {
      background: #fafafa;
    }

    :deep(.ant-table-wrapper .ant-table-tbody >tr >td >.ant-table-wrapper:only-child .ant-table) {
      margin-block: 0 !important;
    }

    :deep(.ant-table.ant-table-empty) {
      margin-block: 0 !important;
    }

    :deep(thead th) {
      color: var(--grey-text);
      border-bottom: unset;
    }
  }

  .orders-table {
    position: relative;
  }

  .expand-header-gear {
    position: absolute;
    top: 8px;
    left: 10px;
    z-index: 1;
    font-size: 14px;
    color: var(--grey-text);
  }

  .user-link {
    cursor: pointer;
    color: var(--main-green);
  }

  .user-link:hover {
    color: var(--hover-color);
  }
</style>